import React, { useEffect, useState } from 'react';
import { TransactionType } from '@klever/sdk-web';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import { sendMegaphone, executeMegaphone } from '@/redux/thunks/chatThunks';
import { buildSignBroadcast } from '@/utils/kleverTx';
import api from '@/utils/api';
import { toast } from 'react-toastify';
import './MegaphoneComposer.scss';

const MAX_CHARS = 200;

interface Props {
  onClose: () => void;
}

type MegaphoneScope = 'local' | 'global';

interface MegaphoneFeeInfo {
  feeKlyx: number; // legacy field, equals fees.local
  fees: { local: number; global: number };
  paymentWallet: string | null;
  tokenId: string;
}

const MegaphoneComposer: React.FC<Props> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const authMethod = useAppSelector((state) => state.auth.user?.authMethod);
  const activeRoomId = useAppSelector((state) => state.chat.activeRoomId);
  const activeRoomName = useAppSelector((state) => {
    const r = state.chat.rooms.find((x) => x.id === state.chat.activeRoomId);
    return r?.name ?? '';
  });
  const isEmailUser = authMethod === 'email';
  const [body, setBody] = useState('');
  const [scope, setScope] = useState<MegaphoneScope>('local');
  const [submitting, setSubmitting] = useState(false);
  const [feeInfo, setFeeInfo] = useState<MegaphoneFeeInfo | null>(null);
  const currentFee = feeInfo
    ? scope === 'global' ? feeInfo.fees.global : feeInfo.fees.local
    : null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<MegaphoneFeeInfo>('/chat/megaphone/fee');
        if (!cancelled) setFeeInfo(data);
      } catch (err) {
        console.error('[Megaphone] failed to load fee info', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const remaining = MAX_CHARS - body.length;

  const handleSend = async () => {
    const trimmed = body.trim();
    if (!trimmed || submitting) return;

    if (!feeInfo?.paymentWallet) {
      toast.error('Megaphone payments are not configured yet. Try again later.');
      return;
    }
    if (!activeRoomId) {
      toast.error('Open a chat room before sending a megaphone.');
      return;
    }
    if (currentFee === null) return;

    setSubmitting(true);
    try {
      if (isEmailUser) {
        await dispatch(executeMegaphone({ body: trimmed, roomId: activeRoomId, scope })).unwrap();
      } else {
        const txHash = await buildSignBroadcast({
          contractType: TransactionType.Transfer,
          payload: {
            receiver: feeInfo.paymentWallet,
            // KLV has 6 decimals → smallest unit. Fee depends on scope:
            // local = chatService.MEGAPHONE_FEE_LOCAL, global =
            // chatService.MEGAPHONE_FEE_GLOBAL.
            amount: Math.floor(currentFee * 1_000_000),
            kda: feeInfo.tokenId,
          },
          kdaFee: feeInfo.tokenId,
        });
        await dispatch(sendMegaphone({ body: trimmed, txHash, roomId: activeRoomId, scope })).unwrap();
      }
      // No success toast — the broadcast message appearing in the room is
      // the confirmation. The wallet/SC flow's own "transaction confirmed"
      // toast covered ours anyway.
      onClose();
    } catch (err: any) {
      // Surface the backend's structured message when present
      // (toApiError shape: { error, message, category, technicalDetails }).
      // Falls back to axios response.data.error, then the raw error
      // message. Important because the wallet flow's broadcast errors
      // and the backend's insufficient-funds responses both flow through
      // this catch.
      const backend = err?.response?.data;
      const message =
        backend?.message || backend?.error || err?.message || 'Failed to send megaphone';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="megaphone-composer">
      <div className="megaphone-composer__header">
        <span className="megaphone-composer__icon">📣</span>
        <h3 className="megaphone-composer__title">Megaphone</h3>
        <p className="megaphone-composer__subtitle">
          {scope === 'local'
            ? `Your message will appear in #${activeRoomName || 'this room'} only.`
            : `Your message will appear inline in #${activeRoomName || 'this room'} AND as a banner in every other room.`}
        </p>
      </div>

      {feeInfo && (
        <div className="megaphone-composer__scope" role="radiogroup" aria-label="Megaphone scope">
          <button
            type="button"
            role="radio"
            aria-checked={scope === 'local'}
            className={`megaphone-composer__scope-option ${scope === 'local' ? 'active' : ''}`}
            onClick={() => setScope('local')}
            disabled={submitting}
          >
            <span className="scope-label">In this room</span>
            <span className="scope-fee">{feeInfo.fees.local} KLV</span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={scope === 'global'}
            className={`megaphone-composer__scope-option ${scope === 'global' ? 'active' : ''}`}
            onClick={() => setScope('global')}
            disabled={submitting}
          >
            <span className="scope-label">All rooms (banner)</span>
            <span className="scope-fee">{feeInfo.fees.global} KLV</span>
          </button>
        </div>
      )}

      <textarea
        className="megaphone-composer__input"
        placeholder="Type your message…"
        maxLength={MAX_CHARS}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        autoFocus
      />

      <div className="megaphone-composer__footer">
        <span className={`megaphone-composer__counter ${remaining < 20 ? 'warning' : ''}`}>
          {remaining}
        </span>
        <div className="megaphone-composer__actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="btn btn-megaphone"
            onClick={handleSend}
            disabled={!body.trim() || submitting || !feeInfo?.paymentWallet}
          >
            {submitting ? 'Sending…' : '📣 Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MegaphoneComposer;
